import React, { useState, useEffect } from "react";
import UseAxios from "../Auth/UseAxios";
import { EMPLOYEE_API_BASE_URL } from "../Auth/Api";
import { useAuth } from "../Auth/AuthContext";
import Base from "../Employee_components/Employee_Base";
import {
  Breadcrumbs,
  Link,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import "./AddEmployees.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const AddEmployees = () => {
  const api = UseAxios();
  const { employee } = useAuth();
  const [documentFile, setDocumentFile] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableDesignations, setAvailableDesignations] = useState([]);
  const navigate = useNavigate();

  const hierarchy = {
    GM: ["AGM", "DO", "DM", "MO"],
    AGM: ["DO", "DM", "MO"],
    DO: ["DM", "MO"],
    DM: ["MO"],
    MO: [""],
  };

  useEffect(() => {
    if (employee && employee.designation) {
      setAvailableDesignations(hierarchy[employee.designation] || []);
    }
  }, [employee]);

  const [formData, setFormData] = useState({
    fullName: "",
    dateOfBirth: "",
    email: "",
    mobile: "",
    location: "",
    designation: "",
    address: "",
    username: "",
    password: "",
    documentName: "",
    managerReferenceId: "",
  });

  // Fetch existing employees to assign a manager
  const fetchManagers = async () => {
    try {
      const response = await api.get(
        `${EMPLOYEE_API_BASE_URL}/employee/hierarchy/${employee.employeeReferenceId}`
      );
      setManagers(response.data);
    } catch (err) {
      console.error("Error fetching managers:", err);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setDocumentFile(e.target.files[0]);
  };

  // Validate form inputs
  const validateForm = () => {
    const validationErrors = {};
    if (!formData.fullName.trim()) validationErrors.fullName = "Full name is required";
    if (!formData.dateOfBirth) validationErrors.dateOfBirth = "Date of birth is required";
    if (!formData.email) validationErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) validationErrors.email = "Invalid email format";
    if (!formData.mobile) validationErrors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(formData.mobile))
      validationErrors.mobile = "Invalid phone number (10 digits required)";
    if (!formData.location.trim()) validationErrors.location = "Location is required";
    if (!formData.designation) validationErrors.designation = "Designation is required";
    if (!formData.address.trim()) validationErrors.address = "Address is required";
    if (!formData.username.trim()) validationErrors.username = "Username is required";
    if (!formData.password) validationErrors.password = "Password is required";
    else if (formData.password.length < 6)
      validationErrors.password = "Password must be at least 6 characters";
    if (!formData.documentName.trim())
      validationErrors.documentName = "Document name is required";
    if (!documentFile) validationErrors.documentFile = "Document file is required";
    if (!formData.managerReferenceId)
      validationErrors.managerReferenceId = "Please assign a manager";

    return validationErrors;
  };

  // Submit employee data to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("employee", new Blob([JSON.stringify(formData)], { type: "application/json" }));
    if (documentFile) data.append("documentFile", documentFile);

    try {
      const response = await api.post(`${EMPLOYEE_API_BASE_URL}/employee/add-employee`, data);

      if (response.status === 200) {
        await Swal.fire({
          icon: "success",
          title: "Employee Added!",
          text: "The employee has been successfully added.",
          confirmButtonColor: "#3085d6",
        });
        setFormData({
          fullName: "",
          dateOfBirth: "",
          email: "",
          mobile: "",
          location: "",
          designation: "",
          address: "",
          username: "",
          password: "",
          documentName: "",
          managerReferenceId: "",
        });
        setDocumentFile(null);
        navigate("/employee/employee_list");
      }
    } catch (error) {
      console.error("Error adding employee:", error);

      let message = "There was an unexpected error adding the employee.";
      if (error.response && error.response.data) {
        message = error.response.data.message || message;
      }

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: message,
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Base>
      <div className="pt-3 mt-5" style={{ display: "flex", justifyContent: "flex-end", paddingRight: "20px" }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            color="inherit"
            href="/employee/dashboard"
            sx={{ color: "blue", fontWeight: "bold" }}
          >
            Dashboard
          </Link>
          <Link
            underline="none"
            color="inherit"
            href="/employee/add_employee"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            Add Employee
          </Link>
        </Breadcrumbs>
      </div>

      <h2 className="text-center fw-bold fs-1" style={{ color: "darkslategrey" }}>
        Add Employee
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <hr style={{ width: "90%" }} />
      </div>

      <div className="container d-flex justify-content-center align-items-center">
        <form
          className="p-4 border rounded shadow-sm bg-light w-100"
          style={{ maxWidth: "900px", marginTop: "10px" }}
          onSubmit={handleSubmit}
        >
          {/* Full Name */}
          <div className="row mb-3">
            <label htmlFor="fullName" className="col-sm-3 col-form-label emp-label">
              Employee Full Name:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                id="fullName"
                name="fullName"
                placeholder="Employee full name"
                value={formData.fullName}
                onChange={handleChange}
              />
              {errors.fullName && <small className="text-danger">{errors.fullName}</small>}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="row mb-3">
            <label htmlFor="dateOfBirth" className="col-sm-3 col-form-label emp-label">
              Date of Birth*:
            </label>
            <div className="col-sm-7">
              <input
                type="date"
                className="form-control"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
              {errors.dateOfBirth && <small className="text-danger">{errors.dateOfBirth}</small>}
            </div>
          </div>

          {/* Email */}
          <div className="row mb-3">
            <label htmlFor="email" className="col-sm-3 col-form-label emp-label">
              Email Id*:
            </label>
            <div className="col-sm-7">
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <small className="text-danger">{errors.email}</small>}
            </div>
          </div>

          {/* Mobile */}
          <div className="row mb-3">
            <label htmlFor="mobile" className="col-sm-3 col-form-label emp-label">
              Phone Number*:
            </label>
            <div className="col-sm-7">
              <input
                type="tel"
                className="form-control"
                id="mobile"
                name="mobile"
                placeholder="Phone number"
                value={formData.mobile}
                onChange={handleChange}
              />
              {errors.mobile && <small className="text-danger">{errors.mobile}</small>}
            </div>
          </div>

          {/* Location */}
          <div className="row mb-3">
            <label htmlFor="location" className="col-sm-3 col-form-label emp-label">
              Location:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                id="location"
                name="location"
                placeholder="Location"
                value={formData.location}
                onChange={handleChange}
              />
              {errors.location && <small className="text-danger">{errors.location}</small>}
            </div>
          </div>

          {/* Designation */}
          <div className="row mb-3">
            <label htmlFor="designation" className="col-sm-3 col-form-label emp-label">
              Designation:
            </label>
            <div className="col-sm-7">
              <select
                className="form-control"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              >
                <option value="">Select Designation</option>
                {availableDesignations.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              {errors.designation && <small className="text-danger">{errors.designation}</small>}
            </div>
          </div>

          {/* Address */}
          <div className="row mb-3">
            <label htmlFor="address" className="col-sm-3 col-form-label emp-label">
              Address:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="address"
                placeholder="Enter address"
                value={formData.address}
                onChange={handleChange}
              />
              {errors.address && <small className="text-danger">{errors.address}</small>}
            </div>
          </div>

          {/* Username */}
          <div className="row mb-3">
            <label htmlFor="username" className="col-sm-3 col-form-label emp-label">
              Username:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="username"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
              />
              {errors.username && <small className="text-danger">{errors.username}</small>}
            </div>
          </div>

          {/* Password */}
          <div className="row mb-3">
            <label htmlFor="password" className="col-sm-3 col-form-label emp-label">
              Password:
            </label>
            <div className="col-sm-7">
              <input
                type="password"
                className="form-control"
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
              />
              {errors.password && <small className="text-danger">{errors.password}</small>}
            </div>
          </div>

          {/* Document Name */}
          <div className="row mb-3">
            <label htmlFor="documentName" className="col-sm-3 col-form-label emp-label">
              Document Name*:
            </label>
            <div className="col-sm-7">
              <input
                type="text"
                className="form-control"
                name="documentName"
                placeholder="Enter document name"
                value={formData.documentName}
                onChange={handleChange}
              />
              {errors.documentName && <small className="text-danger">{errors.documentName}</small>}
            </div>
          </div>

          {/* Upload Document */}
          <div className="row mb-3">
            <label htmlFor="documentFile" className="col-sm-3 col-form-label emp-label">
              Upload Document*:
            </label>
            <div className="col-sm-7">
              <input type="file" className="form-control" onChange={handleFileChange} />
              {errors.documentFile && <small className="text-danger">{errors.documentFile}</small>}
            </div>
          </div>

          {/* Assign To */}
          <div className="row mb-3">
            <label htmlFor="managerReferenceId" className="col-sm-3 col-form-label emp-label">
              Assign to*:
            </label>
            <div className="col-sm-7">
              <select
                className="form-control"
                name="managerReferenceId"
                value={formData.managerReferenceId}
                onChange={handleChange}
              >
                <option value="">Select Manager</option>
                <option value={employee.employeeReferenceId}>
                  {employee.fullName} ({employee.designation})
                </option>
                {managers.map((m) => (
                  <option key={m.employeeReferenceId} value={m.employeeReferenceId}>
                    {m.fullName} ({m.designation})
                  </option>
                ))}
              </select>
              {errors.managerReferenceId && (
                <small className="text-danger">{errors.managerReferenceId}</small>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="emp_btn">
            <button type="submit" className="emp-btn" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </Base>
  );
};

export default AddEmployees;
